// components/auth/employee-id-login.tsx
// Spec 0022, AC-3: a secondary, collapsed entry point on the existing sign
// in page for employees with no company email. Microsoft SSO stays the
// primary, default option; this never touches its code path. Typing an
// employee number branches three ways (approved / unregistered / pending),
// decided server side via GET /api/employee-login/status.
"use client";

import { Button } from "@/components/ui/button";
import { SegmentedDigitInput } from "@/components/auth/segmented-digit-input";
import SigningInOverlay from "@/components/auth/signing-in-overlay";
import { LanguageType } from "@/lib/types";
import { _t } from "@/lib/utils";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";

// Mirrors RESEND_COOLDOWN_MS in lib/auth/employeeIdOtp.ts.
const RESEND_COOLDOWN_SECONDS = 60;

type Step =
  | "collapsed"
  | "enter-id"
  | "request-code"
  | "enter-code"
  | "self-register-email"
  | "self-register-verify"
  | "pending-approval";

type StatusState =
  | "approved"
  | "unregistered"
  | "pending_verification"
  | "pending_approval";

export function EmployeeIdLogin({
  language,
  onExpandedChange,
}: {
  language: LanguageType;
  /** Fires whenever this flow opens/collapses, so the parent can hide the
      Microsoft sign-in option and swap the header label while it's active. */
  onExpandedChange?: (expanded: boolean) => void;
}) {
  const [step, setStep] = useState<Step>("collapsed");
  useEffect(() => {
    onExpandedChange?.(step !== "collapsed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const [employeeId, setEmployeeId] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Neutral confirmation after a resend, plus a visible countdown matching
  // the server's own 60s cooldown so the button cannot be hammered into
  // silently absorbed requests.
  const [notice, setNotice] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const reset = () => {
    setStep("collapsed");
    setEmployeeId("");
    setPersonalEmail("");
    setCode("");
    setError(null);
    setNotice(null);
    setResendIn(0);
  };

  const checkStatus = async (idOverride?: string) => {
    // idOverride: SegmentedDigitInput's onComplete fires synchronously, in
    // the same tick as the setEmployeeId() that's still pending -- reading
    // the `employeeId` state here would see the previous, one-digit-short
    // value (the exact bug handleVerifyAndSignIn's codeOverride already
    // works around below).
    const idToSubmit = (idOverride ?? employeeId).trim();
    if (!idToSubmit) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/employee-login/status?employeeId=${encodeURIComponent(idToSubmit)}`);
      if (!res.ok) {
        setError(_t("employee_login_generic_error", language));
        return;
      }
      const data = (await res.json()) as { state: StatusState };
      if (data.state === "approved") {
        await requestSignInCode(idToSubmit);
        setStep("enter-code");
      } else if (data.state === "unregistered") {
        setStep("self-register-email");
      } else if (data.state === "pending_verification") {
        setStep("self-register-verify");
      } else {
        setStep("pending-approval");
      }
    } catch {
      setError(_t("employee_login_generic_error", language));
    } finally {
      setLoading(false);
    }
  };

  // Resending gave no feedback at all: this fired and swallowed the
  // result, so the button looked dead. Paired with the server's silent 60s
  // cooldown (which deliberately sends no second email and returns the
  // same response either way, spec 0022 AC-5), that left people waiting on
  // an email that never came, then entering a stale code and hitting
  // "Incorrect or expired code". The wording stays deliberately neutral,
  // it must not reveal whether an email actually went out.
  const requestSignInCode = async (idOverride?: string) => {
    setError(null);
    await fetch("/api/employee-login/request-otp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ employeeId: (idOverride ?? employeeId).trim() }),
    }).catch(() => {});
    setNotice(_t("employee_login_code_sent_notice", language));
    setResendIn(RESEND_COOLDOWN_SECONDS);
  };

  const handleVerifyAndSignIn = async (codeOverride?: string) => {
    // codeOverride: SegmentedDigitInput's onComplete fires synchronously,
    // in the same tick as the setCode() that's still pending -- reading
    // the `code` state here would see the previous, one-digit-short value.
    const codeToSubmit = (codeOverride ?? code).trim();
    if (!codeToSubmit) return;
    setError(null);
    setLoading(true);
    try {
      const result = await signIn("credentials", {
        employeeId: employeeId.trim(),
        code: codeToSubmit,
        redirect: false,
      });
      if (result?.error) {
        setError(_t("employee_login_wrong_code", language));
        setLoading(false);
        return;
      }
      // Live incident, 2026-10-01: "logged in [after a] refresh but not
      // redirecting inside" -- router.push() is a client side RSC
      // transition that can race the Set-Cookie this signIn() call just
      // issued, landing on the destination page before useSession() (and
      // the server layout's own cookie read) have picked up the new
      // session, so it looked like nothing happened until a manual
      // reload. The Microsoft SSO button (social.tsx) avoids this
      // entirely with signIn({ redirect: true }), a real server driven
      // redirect; this path keeps redirect: false (so a wrong code shows
      // inline instead of a jarring full reload) but switches to a hard
      // navigation only on the success path, guaranteeing the next
      // request carries the fresh cookie.
      //
      // Follow-up live incident, same day: result?.url (Credentials +
      // redirect: false) does not reliably resolve to a real, locale
      // prefixed destination -- hard navigating to it landed on
      // "?feedback=Your session has expired" even immediately after a
      // fully correct sign in. Confirmed live that the bare site root
      // does NOT have this problem (it runs the locale-detect +
      // authenticated-session redirect that actually lands inside), so
      // this now always targets "/" instead of trusting result.url or a
      // hand built callbackUrl.
      // No setLoading(false) here, deliberately: the overlay above stays
      // up (and keeps blocking clicks) through the hard navigation that
      // just started, rather than flashing away the instant this handler
      // returns while the actual page unload is still pending.
      window.location.href = "/";
    } catch {
      setError(_t("employee_login_generic_error", language));
      setLoading(false);
    }
  };

  const handleSelfRegisterSubmit = async () => {
    if (!personalEmail.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/employee-login/self-register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employeeId: employeeId.trim(), personalEmail: personalEmail.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || _t("employee_login_generic_error", language));
        return;
      }
      setStep("self-register-verify");
    } catch {
      setError(_t("employee_login_generic_error", language));
    } finally {
      setLoading(false);
    }
  };

  const handleSelfRegisterVerify = async (codeOverride?: string) => {
    const codeToSubmit = (codeOverride ?? code).trim();
    if (!codeToSubmit) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/employee-login/self-register/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employeeId: employeeId.trim(), code: codeToSubmit }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || _t("employee_login_wrong_code", language));
        return;
      }
      setStep("pending-approval");
    } catch {
      setError(_t("employee_login_generic_error", language));
    } finally {
      setLoading(false);
    }
  };

  if (step === "collapsed") {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => setStep("enter-id")}
        className="w-full h-12 rounded-full text-base"
      >
        {_t("employee_login_entry_point", language)}
      </Button>
    );
  }

  const inputClass =
    "w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 text-center";
  const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 text-center";

  return (
    <div className="w-full space-y-3 rounded-md border border-gray-200 dark:border-gray-700 p-3 flex flex-col items-center">
      <SigningInOverlay
        visible={step === "enter-code" && loading}
        label={_t("employee_login_signing_in", language)}
      />
      {error && <p className="text-xs text-secondary">{error}</p>}
      {notice && !error && <p className="text-xs text-emerald-600">{notice}</p>}

      {step === "enter-id" && (
        <>
          <div className="flex flex-col items-center">
            <label className={labelClass}>{_t("employee_login_id_label", language)}</label>
            <SegmentedDigitInput
              length={4}
              value={employeeId}
              onChange={setEmployeeId}
              autoFocus
              ariaLabel={_t("employee_login_id_label", language)}
              onComplete={checkStatus}
            />
          </div>
          <div className="flex justify-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={reset}>
              {_t("cancel", language)}
            </Button>
            <Button type="button" size="sm" className="rounded-full" onClick={() => checkStatus()} disabled={loading || employeeId.length !== 4}>
              {loading ? _t("loading", language) : _t("next", language)}
            </Button>
          </div>
        </>
      )}

      {step === "enter-code" && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {_t("employee_login_code_sent_hint", language)}
          </p>
          <div className="flex flex-col items-center">
            <label className={labelClass}>{_t("employee_login_code_label", language)}</label>
            <SegmentedDigitInput
              length={6}
              value={code}
              onChange={setCode}
              autoFocus
              ariaLabel={_t("employee_login_code_label", language)}
              onComplete={handleVerifyAndSignIn}
            />
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={reset}>
                {_t("cancel", language)}
              </Button>
              <Button type="button" size="sm" className="rounded-full" onClick={() => handleVerifyAndSignIn()} disabled={loading || code.length !== 6}>
                {loading ? _t("loading", language) : _t("employee_login_sign_in", language)}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => requestSignInCode()}
              disabled={resendIn > 0}
              className="text-xs text-gray-500 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
            >
              {resendIn > 0
                ? `${_t("employee_login_resend_code", language)} (${resendIn})`
                : _t("employee_login_resend_code", language)}
            </button>
          </div>
        </>
      )}

      {step === "self-register-email" && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {_t("employee_login_self_register_hint", language).replace("{employeeId}", employeeId.trim())}
          </p>
          <div className="w-full">
            <label className={labelClass}>{_t("employee_login_email_label", language)}</label>
            <input
              type="email"
              value={personalEmail}
              onChange={(e) => setPersonalEmail(e.target.value)}
              className={inputClass}
              autoFocus
            />
          </div>
          <div className="flex justify-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={reset}>
              {_t("cancel", language)}
            </Button>
            <Button type="button" size="sm" className="rounded-full" onClick={handleSelfRegisterSubmit} disabled={loading || !personalEmail.trim()}>
              {loading ? _t("loading", language) : _t("employee_login_send_code", language)}
            </Button>
          </div>
        </>
      )}

      {step === "self-register-verify" && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {_t("employee_login_code_sent_hint", language)}
          </p>
          <div className="flex flex-col items-center">
            <label className={labelClass}>{_t("employee_login_code_label", language)}</label>
            <SegmentedDigitInput
              length={6}
              value={code}
              onChange={setCode}
              autoFocus
              ariaLabel={_t("employee_login_code_label", language)}
              onComplete={handleSelfRegisterVerify}
            />
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={reset}>
                {_t("cancel", language)}
              </Button>
              <Button type="button" size="sm" className="rounded-full" onClick={() => handleSelfRegisterVerify()} disabled={loading || code.length !== 6}>
                {loading ? _t("loading", language) : _t("employee_login_verify", language)}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => setStep("self-register-email")}
              className="text-xs text-gray-500 hover:underline"
            >
              {_t("employee_login_change_email", language)}
            </button>
          </div>
        </>
      )}

      {step === "pending-approval" && (
        <>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {_t("employee_login_pending_approval", language)}
          </p>
          <div className="flex justify-center">
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={reset}>
              {_t("employee_login_ok", language)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
